import { Component, OnInit } from '@angular/core';
import { forkJoin, Observable, of as observableOf } from 'rxjs';
import { finalize, catchError } from 'rxjs/operators';
import { MediaObserver } from '@angular/flex-layout';
import * as _ from 'lodash';

import {
  DialogService,
  LanguageService,
  HelperService
} from 'src/app/services';
import { environment } from 'src/environments/environment';

import { AllocationAndReceivingRunningBalanceService } from './allocation-and-receiving-running-balance.service';
import { AllocationAndReceivingRunningBalanceFilterComponent } from './allocation-and-receiving-running-balance-filter/allocation-and-receiving-running-balance-filter.component';

interface AllocationAndReceivingBalance {
  campaign_code: string;
  campaign_description: string;
  distribution_code: string;
  distribution_description: string;
  fullname: string;
  quantity: number;
}

interface Filter {
  module_code: string;
  file_code: null | string;
  web_filter: {
    campaign_id: number;
    distribution_id: number;
    level: number;
    offset: number;
    limit: number;
  };
  filter?: {};
}

@Component({
  selector: 'esc-allocation-and-receiving-running-balance',
  templateUrl: './allocation-and-receiving-running-balance.component.html',
  styleUrls: ['./allocation-and-receiving-running-balance.component.scss']
})
export class AllocationAndReceivingRunningBalanceComponent implements OnInit {
  public columns = [
    { def: 'campaign_code', header: 'Campaign Code' },
    { def: 'campaign_description', header: 'Campaign Description' },
    { def: 'distribution_code', header: 'Distribution Code' },
    { def: 'distribution_description', header: 'Distribution Description' },
    { def: 'username', header: 'Username' },
    { def: 'fullname', header: 'Full Name' },
    { def: 'quantity', header: 'Quantity' },
    { def: 'received', header: 'Received' }
  ];
  public listData: AllocationAndReceivingBalance[] = [];
  public listLength = 0;
  public currentFilters: Filter = _.cloneDeep(INITIAL_OPTIONS);
  public pageSize = 10;
  public page = 1;
  public filterNumber = 0;
  public loading = {
    list: false,
    campaign: false,
    distribution: false,
    export: false,
    error: false
  };
  private networkErrMsg = this.languageService.getTranslation(
    'MSG_ERROR_NETWORK_CONNECTION',
    {}
  );

  private campaigns = [];
  private distributions = [];

  constructor(
    public helper: HelperService,
    public media: MediaObserver,
    public service: AllocationAndReceivingRunningBalanceService,
    private dialogService: DialogService,
    private languageService: LanguageService
  ) {}

  ngOnInit() {
    this.refresh();
  }

  public downloadReport() {
    this.loading.export = true;

    const downloadFilters = _.cloneDeep(this.currentFilters);

    delete downloadFilters.web_filter.limit;
    delete downloadFilters.web_filter.offset;

    downloadFilters.filter = downloadFilters.web_filter;
    delete downloadFilters.web_filter;

    this.service
      .generate(downloadFilters)
      .pipe(
        finalize(() => {
          this.loading.export = false;
          return observableOf(null);
        })
      )
      .subscribe(
        (res: any) => {
          if (environment.showLogs) {
            console.log(res);
          }

          if (!res || res.status == 0 || res.status == 400) {
            this.dialogService.openSnackBar(this.networkErrMsg);
            return;
          } else {
            this.helper.reportGenerated();
          }
        },
        (err) => {
          if (environment.showLogs) {
            console.error(err);
          }
          if (this.listData.length == 0) {
          } else {
            this.dialogService.openSnackBar(
              this.languageService.getTranslation(
                'MSG_ERROR_NETWORK_CONNECTION',
                {}
              )
            );
          }
          return observableOf(null);
        }
      );
  }

  public openFilter() {
    const dialogRef = this.dialogService.openDialog(
      AllocationAndReceivingRunningBalanceFilterComponent,
      {
        width: '500px',
        autoFocus: false,
        disableClose: false,
        data: {
          filters: this.currentFilters,
          campaigns: this.campaigns,
          distributions: this.distributions
        }
      }
    );

    dialogRef
      .afterClosed()
      .subscribe(
        (result: {
          level: number;
          campaign_id: number;
          distribution_id: number;
        }) => {
          if (!result) {
            return;
          }

          const { level, campaign_id, distribution_id } = result;

          if (
            this.currentFilters.web_filter.level === level &&
            this.currentFilters.web_filter.campaign_id === campaign_id &&
            this.currentFilters.web_filter.distribution_id === distribution_id
          ) {
            return;
          }

          this.currentFilters.web_filter.level = level;
          this.currentFilters.web_filter.campaign_id = campaign_id;
          this.currentFilters.web_filter.distribution_id = distribution_id;
          this.fetchAllocationAndReceivingRunningBalance(1);
        }
      );
  }

  public onPageChange(event: { pageIndex: number; pageSize: number }) {
    this.page = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetchAllocationAndReceivingRunningBalance(this.page);
  }

  private fetchAllocationAndReceivingRunningBalance(pageNumber: number) {
    this.loading.list = true;
    this.loading.error = false;

    this.currentFilters.web_filter.limit = this.pageSize;
    this.currentFilters.web_filter.offset = (pageNumber - 1) * this.pageSize;

    this.service
      .getList(this.currentFilters)
      .pipe(
        finalize(() => {
          this.loading.list = false;
          return observableOf(null);
        })
      )
      .subscribe(
        (res: any) => {
          if (!this.isValidResponse(res)) {
            this.handleFetchingError();
            return;
          }

          if (res.value == 'Empty Object') return;

          this.listData = res.data;
          this.listLength = res.count;
          this.updateDisplayedColumns();
        },
        (err) => {
          if (environment.showLogs) {
            console.error(err);
          }
          this.handleFetchingError();
          return observableOf(null);
        }
      );
  }

  private fetchCampaigns(): Observable<any> {
    this.loading.campaign = true;
    return this.service
      .filter({
        web_filter: {
          filter_type: 'campaign',
          filters: { campaign_id: 0, distribution_id: 0 },
          keyword: '',
          level: 1,
          limit: 1,
          offset: 0
        }
      })
      .pipe(
        finalize(() => {
          this.loading.campaign = false;
          return observableOf(null);
        }),
        catchError((err) => {
          console.error(err);
          return observableOf(null);
        })
      );
  }

  private fetchDistributions(): Observable<any> {
    this.loading.distribution = true;
    return this.service
      .filter({
        web_filter: {
          filter_type: 'distribution',
          filters: { campaign_id: 0, distribution_id: 0 },
          keyword: '',
          level: 1,
          limit: 1,
          offset: 0
        }
      })
      .pipe(
        finalize(() => {
          this.loading.distribution = false;
          return observableOf(null);
        }),
        catchError((err) => {
          console.error(err);
          return observableOf(null);
        })
      );
  }

  private processData(response: any, type: 'campaigns' | 'distributions') {
    if (this.isValidResponse(response)) {
      this[type] = [...response.data];
    } else {
      this[type] = [];
      this.dialogService.openSnackBar(this.networkErrMsg);
    }
  }

  private isValidResponse(response: any): boolean {
    return (
      response &&
      response.status !== 0 &&
      response.status !== 400 &&
      _.has(response, 'value') &&
      response.value === 'Success'
    );
  }

  private updateDisplayedColumns() {
    if (this.currentFilters.web_filter.level === 1) {
      this.columns = [
        { def: 'campaign_code', header: 'Campaign Code' },
        { def: 'campaign_description', header: 'Campaign Description' },
        { def: 'distribution_code', header: 'Distribution Code' },
        { def: 'distribution_description', header: 'Distribution Description' },
        { def: 'username', header: 'Username' },
        { def: 'fullname', header: 'Full Name' },
        { def: 'quantity', header: 'Quantity' },
        { def: 'received', header: 'Received' }
      ];
    } else if (this.currentFilters.web_filter.level === 2) {
      this.columns = [
        { def: 'campaign_code', header: 'Campaign Code' },
        { def: 'campaign_description', header: 'Campaign Description' },
        { def: 'distribution_code', header: 'Distribution Code' },
        { def: 'distribution_description', header: 'Distribution Description' },
        { def: 'code', header: 'Code' },
        { def: 'fullname', header: 'Full Name' },
        { def: 'quantity', header: 'Quantity' },
        { def: 'received', header: 'Received' }
      ];
    }
  }

  handleFetchingError() {
    this.loading.error = true;
    this.listData = [];
    this.listLength = 0;
    this.dialogService.openSnackBar(this.networkErrMsg);
  }

  refresh() {
    this.listData = [];
    this.listLength = 0;
    this.currentFilters = _.cloneDeep(INITIAL_OPTIONS);
    forkJoin({
      campaigns: this.fetchCampaigns(),
      distributions: this.fetchDistributions()
    }).subscribe({
      next: ({ campaigns, distributions }) => {
        this.processData(campaigns, 'campaigns');
        this.processData(distributions, 'distributions');
        this.currentFilters.web_filter.campaign_id = this.campaigns[0].id;
        this.currentFilters.web_filter.distribution_id =
          this.distributions[0].id;
        this.fetchAllocationAndReceivingRunningBalance(1);
      },
      error: (err) => {
        console.error('Error fetching data:', err);
      }
    });
  }
}

const INITIAL_OPTIONS = {
  module_code: 'ARRBR',
  file_code: null,
  web_filter: {
    campaign_id: 0,
    distribution_id: 0,
    level: 1,
    limit: 10,
    offset: 0
  }
};
